import { Tab, Tabs, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import uniqid from 'uniqid';
import { Order, OrderLog, PromotionApplied } from '../../apiTypes';
import PrintModal from '../../components/PrintModal';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { createBatch } from '../../utils/api';
import Bill from './Bill';
import PrroReceipt from './Prro/PrroReceipt';

interface Props {
  onClose: () => void;
  order: DbDocument<Order>;
  autoPrint?: boolean;
  discount: number;
  service: number;
  promotionsApplied: PromotionApplied[];
}

const BillPrintModal = (props: Props) => {
  const { onClose, order, autoPrint, discount = 0, service = 0 } = props;
  const { worker, dishesMap, legalEntities, legalEntitiesMap, selfOrderPointsMap } = useCafe();
  const { cafeSlug, cafe } = useAdmin();
  const [tab, setTab] = useState<'regular' | string>('regular');

  const receiptId = order.prroReceipts?.[tab]?.id;
  const cashierToken =
    selfOrderPointsMap[order.selfOrderPointId || '']?.checkboxSettings.cashierToken ||
    legalEntitiesMap[tab]?.prroCheckBox.cashierToken;

  const handlePrint = useCallback(() => {
    if (worker) {
      const batch = createBatch();

      const orderLog: OrderLog = {
        time: Date.now(),
        workerId: worker.id,
        workerName: worker.name,
        type: 'print',
        state: {
          workerId: order.workerId,
          workerName: order.workerName,
          tableId: order.tableId,
          tableName: order.tableName,
          clientId: order.clientId || '',
          clientName: order.clientName || '',
          discount: order.discount || 0,
          items: order.items,
          paid: order.paid,
          status: order.status || null,
          delivery: order.delivery || null,
        },
      };

      batch.set<OrderLog>(`cafes/${cafeSlug}/orders/${order.id}/logs/${uniqid()}`, orderLog);

      batch.commit();
    }
  }, [cafeSlug, order, worker]);

  return (
    <PrintModal
      onClose={onClose}
      onPrint={handlePrint}
      isPosPrinter
      autoPrint={autoPrint}
      header={
        order.prroReceipts &&
        Object.keys(order.prroReceipts).length > 0 && (
          <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable">
            <Tab value="regular" label="Рахунок" />
            {Object.values(order.prroReceipts).map((receipt, index) => (
              <Tab key={receipt.legalEntityId} value={receipt.legalEntityId} label={`Фіскальний чек ${index + 1}`} />
            ))}
          </Tabs>
        )
      }
    >
      {tab === 'regular' && (
        <Bill
          cafe={cafe}
          dishesMap={dishesMap}
          discount={discount}
          service={service}
          items={order.items}
          number={order.number}
          created={order.created}
          delivery={order.delivery}
          comment={order.comment}
          promotionsApplied={props.promotionsApplied}
          legalEntities={legalEntities}
          legalEntitiesMap={legalEntitiesMap}
          waiter={order.workerName}
        />
      )}
      {tab !== 'regular' && receiptId && cafe && cashierToken && (
        <PrroReceipt receiptId={receiptId} cashierToken={cashierToken} />
      )}

      {tab !== 'regular' && !receiptId && (
        <Typography variant="h5" sx={{ textAlign: 'center', color: (t) => t.palette.text.secondary, my: 4 }}>
          Не фіскалізовано!
        </Typography>
      )}
    </PrintModal>
  );
};

export default BillPrintModal;
